/**
 * 任务管理 API 接口
 */
import apiClient from '@/utils/apiClient';
import type {
  Task,
  TaskQueryParams,
  TaskCreateInput,
  TaskUpdateInput,
} from '@/types/task';
import { createRequestDeduper, type RequestDedupOptions } from './request-dedupe';

interface GetTasksResponse {
  data?: Task[];
  total?: number;
}

interface TaskHoursSummaryResponse {
  success?: boolean;
  data?: Record<string, { planned: number; actual: number }>;
  error?: string;
}

interface TasksForHoursEntryResponse {
  success: boolean;
  data?: Task[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  error?: string;
}

async function fetchTasks(
  params: TaskQueryParams = {}
): Promise<{ data: Task[]; total: number }> {
  const searchParams = new URLSearchParams();

  // 使用 snake_case 参数，符合 RESTful API 规范
  if (params.projectId) {
    searchParams.append('project_id', params.projectId);
  }
  if (params.requirementId) {
    searchParams.append('requirement_id', params.requirementId);
  }
  if (params.assigneeId) {
    searchParams.append('assignee_id', params.assigneeId);
  }
  if (params.status) {
    searchParams.append('status', params.status);
  }
  if (params.keyword) {
    searchParams.append('keyword', params.keyword);
  }
  if (params.entityType) {
    searchParams.append('entity_type', params.entityType);
  }
  if (params.includeHoursSummary !== undefined) {
    searchParams.append('include_hours_summary', params.includeHoursSummary.toString());
  }
  if (params.includeEntityNames !== undefined) {
    searchParams.append('include_entity_names', params.includeEntityNames.toString());
  }
  if (params.skipCount !== undefined) {
    searchParams.append('skip_count', params.skipCount.toString());
  }
  if (params.preserveDbOrder !== undefined) {
    searchParams.append('preserve_db_order', params.preserveDbOrder.toString());
  }
  if (params.bypassCache !== undefined) {
    searchParams.append('bypass_cache', params.bypassCache.toString());
  }
  if (params.showArchived !== undefined) {
    searchParams.append('show_archived', params.showArchived.toString());
  }
  if (params.current) {
    searchParams.append('current', params.current.toString());
  }
  if (params.pageSize) {
    searchParams.append('pageSize', params.pageSize.toString());
  }

  const response = await apiClient.get(`/tasks?${searchParams.toString()}`);

  const responseData = response as unknown as GetTasksResponse;
  const tasks = Array.isArray(responseData?.data) ? responseData.data : [];
  const total = responseData?.total || 0;

  return {
    data: tasks,
    total,
  };
}

const dedupedGetTasks = createRequestDeduper<TaskQueryParams, { data: Task[]; total: number }>(
  fetchTasks,
  { defaultTtlMs: 1200 }
);

async function fetchTaskHoursSummary(params: {
  projectId: string;
  taskIds: string[];
}): Promise<Record<string, { planned: number; actual: number }>> {
  if (!params.projectId || params.taskIds.length === 0) {
    return {};
  }

  const response = (await apiClient.post('/tasks/hours-summary', {
    project_id: params.projectId,
    task_ids: params.taskIds,
  })) as unknown as TaskHoursSummaryResponse;

  if (response.success && response.data) {
    return response.data;
  }

  return {};
}

const dedupedGetTaskHoursSummary = createRequestDeduper<
  { projectId: string; taskIds: string[] },
  Record<string, { planned: number; actual: number }>
>(fetchTaskHoursSummary, {
  defaultTtlMs: 60 * 1000,
  keyBuilder: (params) => JSON.stringify({
    projectId: params.projectId,
    taskIds: [...params.taskIds].sort(),
  }),
});

/**
 * 获取任务列表
 */
export async function getTasks(
  params: TaskQueryParams = {},
  options: RequestDedupOptions = {}
): Promise<{ data: Task[]; total: number }> {
  return dedupedGetTasks(params, options);
}

export async function getTaskHoursSummary(
  projectId: string,
  taskIds: string[],
  options: RequestDedupOptions = {}
): Promise<Record<string, { planned: number; actual: number }>> {
  return dedupedGetTaskHoursSummary(
    { projectId, taskIds },
    options
  );
}

/**
 * 根据ID获取任务详情
 */
export async function getTaskById(id: string): Promise<Task> {
  const response = (await apiClient.get(`/tasks/${id}`)) as unknown as {
    success: boolean;
    data: Task;
  };

  if (!response.success || !response.data) {
    throw new Error('获取任务详情失败');
  }

  return response.data;
}

/**
 * 创建任务
 */
export async function createTask(data: TaskCreateInput): Promise<Task> {
  const response = (await apiClient.post('/tasks', data)) as unknown as {
    success: boolean;
    data: Task;
  };

  if (!response.success || !response.data) {
    throw new Error('创建任务失败');
  }

  return response.data;
}

/**
 * 更新任务
 */
export async function updateTask(
  id: string,
  data: TaskUpdateInput
): Promise<Task> {
  const response = (await apiClient.put(`/tasks/${id}`, data)) as unknown as {
    success: boolean;
    data: Task;
  };

  if (!response.success || !response.data) {
    throw new Error('更新任务失败');
  }

  return response.data;
}

/**
 * 删除任务
 */
export async function deleteTask(id: string): Promise<void> {
  const response = (await apiClient.delete(`/tasks/${id}`)) as unknown as {
    success: boolean;
  };

  if (!response.success) {
    throw new Error('删除任务失败');
  }
}

/**
 * 获取用户有权限录入工时的任务列表
 *
 * @param params 查询参数
 * @returns 任务列表和分页信息
 */
export async function getTasksForHoursEntry(params: {
  hoursType: 'planned' | 'actual';
  targetUserId: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  data: Task[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}> {
  const searchParams = new URLSearchParams();

  searchParams.append('hoursType', params.hoursType);
  searchParams.append('targetUserId', params.targetUserId);

  if (params.page) {
    searchParams.append('page', params.page.toString());
  }

  if (params.pageSize) {
    searchParams.append('pageSize', params.pageSize.toString());
  }

  const response = await apiClient.get(
    `/tasks/for-hours-entry?${searchParams.toString()}`
  );

  const responseData = response as unknown as TasksForHoursEntryResponse;

  if (!responseData.success) {
    throw new Error(responseData.error || '获取任务列表失败');
  }

  return {
    data: Array.isArray(responseData.data) ? responseData.data : [],
    total: responseData.total || 0,
    page: responseData.page || 1,
    pageSize: responseData.pageSize || 50,
    totalPages: responseData.totalPages || 0,
  };
}
